import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { SessionUser } from '../../../hooks/useSession';
import { Reveal } from '../components/Reveal';

export interface HeroProps {
  user: SessionUser | null;
}

/**
 * Hero — centered manifesto + full-width product asset (Linear / Vercel
 * lineage). The live demo no longer lives here; it has its own dedicated
 * section right below (`<TryItLive />`) so the hero can stay calm while
 * still funneling skeptics into a working sandbox one screen down.
 */
export function Hero({ user }: HeroProps) {
  const ctaTo = user ? '/app' : '/sign-up';
  const ctaLabel = user ? 'Open dashboard →' : 'Try it free →';

  return (
    <section className="relative">
      <div className="mx-auto max-w-[1200px] px-6 pt-16 pb-12 lg:px-10 lg:pt-24 lg:pb-16">
        <div className="mx-auto max-w-5xl text-center">
          <Reveal>
            <h1 className="font-display text-5xl font-semibold leading-[1.04] tracking-[-0.02em] md:text-7xl">
              From rough idea
              <br />
              <span className="text-primary">to polished email.</span>
            </h1>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-foreground mx-auto mt-6 max-w-xl text-lg font-medium tracking-tight md:text-xl">
              Emails that feel ready to send.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-muted-foreground mx-auto mt-3 max-w-xl text-base leading-relaxed">
              SendFast helps you use AI to turn rough ideas into on-brand,
              inbox-ready emails for marketing, outreach, updates, and more.
            </p>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-10 flex items-center justify-center">
              <Button size="lg" asChild>
                <Link to={ctaTo}>{ctaLabel}</Link>
              </Button>
            </div>
          </Reveal>
        </div>

        {/* Hero asset hidden until a real screenshot is ready.
            When ready: drop /public/hero-shot.png, uncomment the block below,
            and delete the `void HeroAsset` line near the bottom of this file. */}
        {/*
        <Reveal delay={300}>
          <div className="mt-14 lg:mt-20">
            <HeroAsset />
          </div>
        </Reveal>
        */}
      </div>
    </section>
  );
}

/**
 * Hero asset slot — fake "browser chrome" wrapping a 16:9 surface that loads
 * `/hero-shot.png` when it exists. While the image is missing (404), the
 * inner placeholder stays visible. Drop a PNG/JPG/WebP at
 * `frontend/public/hero-shot.png` and the page picks it up on next reload —
 * no code edit needed.
 */
function HeroAsset() {
  return (
    <div className="border-border bg-background relative mx-auto max-w-[1100px] overflow-hidden rounded-xl border shadow-[0_40px_80px_-32px_rgba(11,12,15,0.18)]">
      <div className="border-border bg-muted/60 flex items-center gap-1.5 border-b px-4 py-2.5">
        <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
        <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
        <span className="bg-foreground/15 size-2.5 rounded-full" aria-hidden />
        <span className="text-muted-foreground ml-3 select-none text-[11px] tracking-wide">
          sendfast.ai/editor
        </span>
      </div>

      <div className="bg-muted/30 relative aspect-[16/9]">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="bg-foreground/8 ring-foreground/8 mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl ring-1">
            <span className="text-muted-foreground text-lg">⌘</span>
          </div>
          <div className="font-display text-muted-foreground text-sm font-medium">
            Hero screenshot
          </div>
          <div className="text-muted-foreground/80 mt-1 text-xs">
            Drop one at <code className="font-mono">frontend/public/hero-shot.png</code>
          </div>
        </div>

        <img
          src="/hero-shot.png"
          alt="SendFast email editor — write a prompt, get a beautiful email"
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    </div>
  );
}

// HeroAsset is intentionally unused while we wait for the screenshot.
// Delete this line when re-enabling the asset block in <Hero />.
void HeroAsset;
